import React, { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link, Typography } from '@mui/material';
import moment from 'moment-timezone';
import { find, map, uniq, without } from 'lodash';

import { DataTable, DataTableActions, DataTableColumn } from 'components';
import { ContentEmailHistoryFragment, ContentEmailSpecFragment, UserSegmentFragment } from 'api';
import { generateLink } from 'Links';
import { InfiniteQuery } from 'types/app';

import DeliveryInterval from './Partials/DeliveryInterval';
import SentActions from './SentActions';

const styles = {
  tableBodyRow: {
    height: 80,
  },
} as const;

interface Props extends InfiniteQuery<ContentEmailHistoryFragment> {
  segments: UserSegmentFragment[];
}

const ContentEmailSentTable: FC<Props> = ({
  segments,
  results,
  isExhausted,
  isInitialLoad,
  sentinelRef,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const pagination = {
    isInitialLoad,
    isExhausted,
    sentinelRef,
  };

  const columns: DataTableColumn<ContentEmailHistoryFragment>[] = [
    {
      width: 230,
      headerCell: {
        render: t('common:email'),
      },
      bodyCell: {
        render: history => (
          <Link
            variant="subtitle2"
            component={generateLink(`/admin/emails/${history.spec.id}`)}
            underline="hover"
          >
            {history.spec.title}
          </Link>
        ),
      },
    },
    {
      width: 140,
      headerCell: {
        render: t('contentEmails:frequency'),
      },
      bodyCell: {
        render: history => (
          <DeliveryInterval interval={history.spec.delivery_interval} color="textSecondary" />
        ),
      },
    },
    {
      width: 210,
      headerCell: {
        render: t('common:when'),
      },
      bodyCell: {
        render: history => {
          const mom = moment.unix(history.sent_at);
          const tzName = mom.tz(moment.tz.guess()).format('z');
          const format = `${mom.format('ddd, MMM D, hh:mm A')} (${tzName})`;

          return <Typography color="textSecondary">{format}</Typography>;
        },
      },
    },
    {
      width: 150,
      headerCell: {
        render: t('common:segment_plural'),
      },
      bodyCell: {
        render: history => renderSegmentNames(history.spec.segment_ids),
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('contentEmails:subjectLine'),
      },
      bodyCell: {
        render: history => (
          <Typography sx={theme.mixins.clamp(2)} color="textSecondary">
            {history.spec.subject}
          </Typography>
        ),
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('contentEmails:numOfPosts'),
      },
      bodyCell: {
        render: history => (
          <Typography color="textSecondary">{history.spec.content_specs.length ?? 0}</Typography>
        ),
      },
    },
    {
      width: 200,
      headerCell: {
        render: t('contentEmails:postTypes'),
      },
      bodyCell: {
        render: history => (
          <Typography color="textSecondary">
            {renderPostTypes(history.spec.content_specs)}
          </Typography>
        ),
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('messages:recipients'),
      },
      bodyCell: {
        render: history => (
          <Typography color="textSecondary">
            {history.registered_user_count + history.unregistered_user_count}
          </Typography>
        ),
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:open_plural'),
      },
      bodyCell: {
        render: history => <Typography color="textSecondary">{history.total_opened}</Typography>,
      },
    },
    {
      width: 100,
      headerCell: {
        render: t('common:click_plural'),
      },
      bodyCell: {
        render: history => <Typography color="textSecondary">{history.total_clicks}</Typography>,
      },
    },
  ];

  function renderSegmentNames(emailSegmentIds: ContentEmailSpecFragment['segment_ids']) {
    const segmentList = emailSegmentIds.reduce(
      (list: ContentEmailSpecFragment['segment_ids'], emailId: ContentEmailSpecFragment['id']) => {
        const segment = find(segments, ['id', emailId]);
        return list.concat(segment ? [segment.title] : []);
      },
      []
    );

    if (!segmentList.length) {
      return <Typography color="textSecondary">{t('contentEmails:noSegments')}</Typography>;
    }

    return (
      <Typography noWrap color="textSecondary">
        {segmentList.join(', ')}
      </Typography>
    );
  }

  function renderPostTypes(types: ContentEmailSpecFragment['content_specs']) {
    const uniqPostTypes = without(uniq(map(types, 'content_type')), undefined);
    const capitalizedPostTypes = uniqPostTypes.map(s => {
      return s.charAt(0).toUpperCase() + s.slice(1);
    });
    return capitalizedPostTypes.join(', ').replace(/, ([^,]*)$/, ' and $1');
  }

  const actions: DataTableActions<ContentEmailHistoryFragment> = history => {
    return <SentActions contentEmail={history.spec} />;
  };

  return (
    <DataTable
      actions={actions}
      data={results}
      rowSelection={null}
      columns={columns}
      pagination={pagination}
      sx={styles.tableBodyRow}
    />
  );
};

export default ContentEmailSentTable;
